import React from 'react';
import { toast } from 'react-toastify';
import { jsx } from '@emotion/core';
import theme from '../../theme/theme';
import TitlesTable from './components/TitlesTable';
import TitleModal from './components/TitleModal';
import {
  useTitlesQuery,
  useDelete_TitleMutation,
} from '../../generated/graphql';
import { ITitle } from '../../utils/types';
import UpdateHandlers from '../../utils/handlers';
import Confirm from '../../components/confirm/Confirm';
import Toastify from '../../components/toastify/Toastify';
import { useAuthState } from '../../context/auth';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import { ADMIN } from '../../routes/routes';
/**@jsx jsx*/

const Titles: React.FC = () => {
  const { userId } = useAuthState();
  const [isOpen, setIsOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [selectedTitle, setSelectedTitle] = React.useState<
    number | undefined
  >();
  const { data: titles, loading } = useTitlesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [deleteTitle] = useDelete_TitleMutation();

  const close = React.useCallback(() => {
    setIsOpen(false);
    setSelectedTitle(undefined);
    setShowConfirm(false);
  }, []);

  const edit = (title: ITitle) => {
    setIsOpen(true);
    setSelectedTitle(title.id);
  };

  const confirm = (title: ITitle) => {
    setSelectedTitle(title.id);
    setShowConfirm(true);
  };

  const notify = (success: boolean, message: string) => {
    toast(<Toastify {...{ success, message }} />, {
      position: 'top-right',
      autoClose: 7000,
      progressStyle: {
        background: success ? theme.colors.green[200] : theme.colors.red[200],
      },
    });
  };

  const remove = async () => {
    const response = await deleteTitle({
      variables: {
        id: selectedTitle,
        doneBy: userId,
      },
      update: UpdateHandlers.deleteTitle,
    });
    setShowConfirm(false);

    if (response.data?.update_title?.affected_rows) {
      notify(true, 'Title deleted successfully.');
    } else {
      notify(false, 'failed to delete title.');
    }
  };

  const getTitle = (id?: number) => {
    if (id) {
      const title = titles?.title?.find((title) => title.id === id);
      if (title) {
        return {
          id: title.id,
          name: title.name,
        };
      }
    }
    return;
  };

  const getTitles = (): ITitle[] => {
    return (
      titles?.title?.map((title) => ({
        id: title.id,
        name: title.name,
        createdAt: title.created_at,
        lastChanged: title.updated_at,
      })) || []
    );
  };

  return (
    <React.Fragment>
      {isOpen && (
        <TitleModal onClose={close} titleData={getTitle(selectedTitle)} />
      )}

      {showConfirm && (
        <Confirm
          cancel={{ label: 'cancel', function: close }}
          confirm={{ label: 'ok', function: remove }}
          onClose={close}
          title="Delete Title"
          message="Are you sure you want to delete?"
        />
      )}

      <SectionTitle
        title="Titles"
        action={{
          label: 'Add',
          icon: { name: 'plus', color: theme.colors.white, position: 'after' },
          function: () => {
            setIsOpen(true);
          },
          allowed: [ADMIN],
        }}></SectionTitle>

      <TitlesTable
        isLoading={loading}
        titles={getTitles()}
        actions={[
          { label: 'Delete', action: confirm },
          { label: 'Edit', action: edit },
        ]}
      />
    </React.Fragment>
  );
};

export default Titles;
